import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toAsciiDigits } from '../lib/format';

/** Countries schools in the region use most, with their dialling code and an example local number */
export const COUNTRIES = [
  { iso: 'AE', code: '971', example: '50 123 4567' },
  { iso: 'SA', code: '966', example: '50 123 4567' },
  { iso: 'KW', code: '965', example: '5012 3456' },
  { iso: 'QA', code: '974', example: '3312 3456' },
  { iso: 'BH', code: '973', example: '3600 1234' },
  { iso: 'OM', code: '968', example: '9212 3456' },
  { iso: 'JO', code: '962', example: '7 9012 3456' },
  { iso: 'EG', code: '20', example: '100 123 4567' },
  { iso: 'LB', code: '961', example: '71 123 456' },
  { iso: 'IQ', code: '964', example: '790 123 4567' },
  { iso: 'MA', code: '212', example: '650 123456' },
  { iso: 'US', code: '1', example: '555 123 4567' },
  { iso: 'GB', code: '44', example: '7400 123456' },
  { iso: 'IN', code: '91', example: '81234 56789' },
  { iso: 'PK', code: '92', example: '301 2345678' },
] as const;
type Iso = (typeof COUNTRIES)[number]['iso'];

const TZ_COUNTRY: Record<string, Iso> = {
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Kuwait': 'KW', 'Asia/Qatar': 'QA', 'Asia/Bahrain': 'BH', 'Asia/Muscat': 'OM',
  'Asia/Amman': 'JO', 'Africa/Cairo': 'EG', 'Asia/Beirut': 'LB', 'Asia/Baghdad': 'IQ', 'Africa/Casablanca': 'MA',
  'Europe/London': 'GB', 'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN', 'Asia/Karachi': 'PK',
};

/** A guess from the device's time zone (UAE when unknown) */
function guessCountry(): Iso {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
  if (tz.startsWith('America/')) return 'US';
  return 'AE';
}

/** 🇦🇪 from "AE" */
const flag = (iso: string) => String.fromCodePoint(...[...iso].map((c) => 0x1f1a5 + c.charCodeAt(0)));

/**
 * Mobile number with a country code picker. Reports the number in international form (+971501234567).
 * Typing a full number starting with + or 00 also works and ignores the picker.
 */
export function PhoneField({ id, onChange, required }: { id?: string; onChange: (e164: string) => void; required?: boolean }) {
  const { i18n } = useTranslation();
  const [iso, setIso] = useState<Iso>(() => {
    try {
      return (localStorage.getItem('pickup.country') as Iso | null) ?? guessCountry();
    } catch {
      return guessCountry();
    }
  });
  const [local, setLocal] = useState('');
  const country = COUNTRIES.find((c) => c.iso === iso) ?? COUNTRIES[0];
  const names = new Intl.DisplayNames([i18n.language === 'ar' ? 'ar' : 'en'], { type: 'region' });

  useEffect(() => {
    const raw = toAsciiDigits(local).replace(/[^\d+]/g, '');
    if (raw.startsWith('+')) onChange(`+${raw.slice(1).replace(/\D/g, '')}`);
    else if (raw.startsWith('00')) onChange(`+${raw.slice(2)}`);
    else onChange(raw ? `+${country.code}${raw.replace(/^0+/, '')}` : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, country.code]);

  return (
    <span className="phone-field" dir="ltr">
      <span className="phone-country">
        <span className="phone-flag" aria-hidden="true">
          {flag(country.iso)}
        </span>
        <span className="phone-caret" aria-hidden="true" />
        <select
          value={iso}
          aria-label={names.of(iso) ?? iso}
          onChange={(e) => {
            setIso(e.target.value as Iso);
            try {
              localStorage.setItem('pickup.country', e.target.value);
            } catch {
              /* not essential */
            }
          }}
        >
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.iso}>
              {flag(c.iso)} {names.of(c.iso)} (+{c.code})
            </option>
          ))}
        </select>
      </span>
      <span className="phone-code">+{country.code}</span>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        required={required}
        value={local}
        placeholder={country.example}
        onChange={(e) => setLocal(e.target.value)}
      />
    </span>
  );
}
